function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;  // wizzy fixed error of default here
    requestAnimationFrame(() => {
      const rect = this.header.getBoundingClientRect();
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(rect.bottom - this.borderOffset)}px`
      );
    });
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', throttle(this.update.bind(this), 100));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

initPages() {
  const sliderWidth = this.slider.clientWidth;

  this.sliderItemsToShow = Array.from(this.sliderItems).filter(el => el.offsetParent !== null);

  if (this.sliderItemsToShow.length < 2) return;

  const first = this.sliderItemsToShow[0];
  const second = this.sliderItemsToShow[1];

  const firstOffset = first.offsetLeft;
  const secondOffset = second.offsetLeft;

  this.sliderItemOffset = secondOffset - firstOffset;

  this.slidesPerPage = Math.floor((sliderWidth - firstOffset) / this.sliderItemOffset);

  this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;

  requestAnimationFrame(() => this.update());
}

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}
// home page js code start

// category slider common js
function initConcernSwiper(scope = document) {
  const concernSwipers = scope.querySelectorAll('.concernSwiper');
  concernSwipers.forEach((swiperEl) => {
    if (swiperEl.classList.contains('swiper-initialized')) return;
    const sectionId = swiperEl.dataset.sectionId;
    new Swiper(swiperEl, {
      slidesPerView: parseInt(swiperEl.dataset.slidesMobile),
      spaceBetween: parseInt(swiperEl.dataset.spaceMobile),
      loop: swiperEl.dataset.loop === "true",
      autoplay: swiperEl.dataset.autoplay === "true" ? {
        delay: parseInt(swiperEl.dataset.autoplayDelay),
        disableOnInteraction: false
      } : false,
      pagination: {
        el: `.swiper-pagination-${sectionId}`,
        clickable: true
      },
      navigation: {
        nextEl: `.swiper-button-next-${sectionId}`,
        prevEl: `.swiper-button-prev-${sectionId}`
      },
      breakpoints: {
        750: {
          slidesPerView: parseInt(swiperEl.dataset.slidesTablet),
          spaceBetween: parseInt(swiperEl.dataset.spaceTablet)
        },
        990: {
          slidesPerView: parseInt(swiperEl.dataset.slidesDesktop),
          spaceBetween: parseInt(swiperEl.dataset.spaceDesktop)
        }
      }
    });
  });
}
// Home page collection tab section js
function initCollectionTabs(scope = document) {
  const sections = scope.querySelectorAll('.collection-tabs');
  sections.forEach((section) => {
    const tabs = section.querySelectorAll('.tab-button-new');
    const sliders = section.querySelectorAll('.js-tab-swiper');
    sliders.forEach((slider) => {
      if (slider.classList.contains('swiper-initialized')) return;
      new Swiper(slider, {
        slidesPerView: 3,
        spaceBetween: 20,
        navigation: {
          nextEl: slider.querySelector('.swiper-button-next'),
          prevEl: slider.querySelector('.swiper-button-prev')
        },
        breakpoints: {
          320: { slidesPerView: 2 },
          768: { slidesPerView: 3 },
          1024: { slidesPerView: 4 }
        }
      });
    });
    // Tab Click
    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const activeTab = tab.dataset.tab;
        tabs.forEach(btn => btn.classList.remove('active'));
        tab.classList.add('active');
        sliders.forEach(slider => {
          slider.classList.toggle('active', slider.id === activeTab);
          if (slider.swiper) {  slider.swiper.update(); }
        });

      });
    });

  });
}
// collection tab code
// collection tab code
function initFeaturedTabs(scope = document) {
  const sections = scope.querySelectorAll('.js-featured-tabs');
  sections.forEach(section => {
    const swiperEl = section.querySelector('.js-featured-swiper');
    if (!swiperEl) return;
    // Prevent double init
    if (swiperEl.classList.contains('swiper-initialized')) return;
    const tabs = section.querySelectorAll('.collection-tab');
    const slides = section.querySelectorAll('.tab-slide');
    const swiper = new Swiper(swiperEl, {
      slidesPerView: 1.4,
      spaceBetween: 15,
      // speed: 600,
      // freeMode: true,
      grabCursor: true,
      watchSlidesProgress: true,
      navigation: {
        nextEl: swiperEl.querySelector('.swiper-button-next'),
        prevEl: swiperEl.querySelector('.swiper-button-prev'),
      },
      breakpoints: {
        320: { slidesPerView: 1.4, spaceBetween: 15 },
        640: { slidesPerView: 1.4, spaceBetween: 15 },
        768: { slidesPerView: 2, spaceBetween: 15 },
        1024: { slidesPerView: 4.2, spaceBetween: 20 }
      }
    });
    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        tabs.forEach(t => t.classList.remove('active'));
        tab.classList.add('active');
        const tabId = tab.dataset.tab;
        slides.forEach(slide => {
          slide.classList.toggle('hidden', slide.dataset.tab !== tabId);
        });
        swiper.update();
        swiper.slideTo(0);

      });
    });
  });
}
// home page hero slider 
function initMainSliders(scope = document) {
  const sliders = scope.querySelectorAll('.js-home-swiper');
  sliders.forEach(slider => {
    if (slider.swiper) return;
    const loop = slider.dataset.loop === "true";
    const autoplay = slider.dataset.autoplay === "true";
    const delay = parseInt(slider.dataset.delay) || 5000;
    const speed = parseInt(slider.dataset.speed) || 600;
    const effect = slider.dataset.effect;
    const disable = slider.dataset.disable === "true";
    const showPagination = slider.dataset.pagination === "true";
    const showNavigation = slider.dataset.navigation === "true";
    const nextBtn = slider.querySelector('.swiper-button-next');
    const prevBtn = slider.querySelector('.swiper-button-prev');
    new Swiper(slider, {
      loop,
      speed,
      effect,
      slidesPerView: 1,
      spaceBetween: 0,
      // preloadImages: false,
      // lazy: true,

      autoplay: autoplay ? {
        delay,
        disableOnInteraction: disable
      } : false,

      pagination: showPagination ? {
        el: slider.querySelector('.swiper-pagination'),
        clickable: true
      } : false,

      navigation: showNavigation ? {
      nextEl: nextBtn,
      prevEl: prevBtn
    } : false
    ,
    on: {
      init: function () {
        playVideo(this);
      },
      slideChange: function () {
        playVideo(this);
      }
    }
    });
  });
}
// Initial Load
document.addEventListener('DOMContentLoaded', () => {
  requestAnimationFrame(() => {
    initMainSliders();
    initCollectionTabs();
    initConcernSwiper();
    initFeaturedTabs();
  });
});
// Shopify Theme Editor Support
document.addEventListener('shopify:section:load', (e) => {
  //initMainSliders(e.target);
  initConcernSwiper(e.target);
  initCollectionTabs(e.target);
  initFeaturedTabs(e.target);
});
//  home page js code end

function initProductDrawers() {
  document.addEventListener('click', function (e) {
    const trigger = e.target.closest('.product-drawer-trigger');
    if (!trigger) return;
    const drawerId = trigger.dataset.drawer;
    const drawer = document.getElementById(drawerId);
    if (drawer) {
      drawer.classList.add('active');
      document.body.classList.add('drawer-open');
    }
  });
  document.addEventListener('click', function (e) {
    const closeBtn = e.target.closest('.drawer-close');
    if (!closeBtn) return;
    const drawer = closeBtn.closest('.product-drawer');
    if (drawer) {
      drawer.classList.remove('active');
      document.body.classList.remove('drawer-open');
    }
  });
  // CLOSE DRAWER (overlay)
  document.addEventListener('click', function (e) {
    if (!e.target.classList.contains('drawer-overlay')) return;
    const drawer = e.target.closest('.product-drawer');
    if (drawer) {
      drawer.classList.remove('active');
      document.body.classList.remove('drawer-open');
    }
  });
}
document.addEventListener('DOMContentLoaded', function () {  if (typeof initProductDrawers === 'function') { initProductDrawers(); } });
document.addEventListener('DOMContentLoaded', () => {
  const mobileTrigger = document.querySelector('.mobile-search-trigger');
  const searchDetails = document.querySelector( '.header__icons details-modal.header__search details');
  if (!mobileTrigger || !searchDetails) return;
  mobileTrigger.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    searchDetails.setAttribute('open', 'true');
    const input = searchDetails.querySelector('.search__input');
    if (input) {  setTimeout(() => input.focus(), 100); }
  });
});
document.addEventListener('click', function (e) {
  const fitLink = e.target.closest('.js-fit-link');
  if (!fitLink) return;
  const wrapper = fitLink.closest('.product-form__input');
  const selectedValueEl = wrapper?.querySelector('[data-selected-value]');
  if (!selectedValueEl) return;
  // update label text
  selectedValueEl.textContent = fitLink.dataset.fitValue;
  // active state
  wrapper.querySelectorAll('.js-fit-link').forEach( link => link.classList.remove('active') );
  fitLink.classList.add('active');
});
async function updateCartCount() {
  try {
    const res = await fetch(window.Shopify.routes.root + 'cart.js', {  cache: 'no-store'  }); // Cache se bachne ke liye
    if (!res.ok) return;
    const cart = await res.json();
    const cartIcons = document.querySelectorAll('#cart-icon-bubble, .header__icon--cart');
    cartIcons.forEach(cartIcon => {
      let bubble = cartIcon.querySelector('.cart-count-bubble');
      if (cart.item_count > 0) {
        if (!bubble) {
          bubble = document.createElement('div');
          bubble.className = 'cart-count-bubble';
          cartIcon.appendChild(bubble);
        }
        bubble.innerHTML = `
          <span aria-hidden="true">${cart.item_count}</span>
          <span class="visually-hidden">${cart.item_count} items</span>
        `;
      } else if (bubble) {
        bubble.remove();
      }
    });
  } catch (e) {
    console.warn('Cart update failed:', e);
  }
}
// Optimized Listeners
const triggerEvents = ['click', 'touchend']; // Mobile touch support
triggerEvents.forEach(eventType => {
  document.addEventListener(eventType, function(e) {
    const isAddBtn = e.target.closest('.product-form__submit, [name="add"], .ajax-submit');
    if (isAddBtn) {
      // 500ms, 1200ms, aur 2500ms par retry - kyunki mobile network slow ho sakta hai
      [500, 1200, 2500].forEach(delay => setTimeout(updateCartCount, delay));
    }
  });
});
/* Initial load & Shopify Events */
document.addEventListener('pageshow',  updateCartCount);
document.addEventListener('cart:updated', updateCartCount);
document.addEventListener('cart:refresh', updateCartCount);
window.addEventListener('pageshow', function(event) {
  // If page is loaded from bfcache
  if (event.persisted) { updateCartCount(); } else { updateCartCount(); }
});
document.addEventListener('click', function (e) {
  const trigger = e.target.closest('.js-size-guide-trigger');
  if (!trigger) return;

  const drawer = document.getElementById('size-guide-drawer');
  if (!drawer) return; // 👈 only real check needed

  drawer.classList.add('active');
  document.body.classList.add('drawer-open');
});

let swiperPromise = null;

function loadSwiperScript() {
  if (swiperPromise) return swiperPromise;

  swiperPromise = new Promise((resolve, reject) => {
    if (typeof Swiper !== 'undefined') {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = window.theme_swiper_url || 'swiper-bundle.js';
    script.async = true;
    script.onload = () => {
      console.log('Swiper loaded dynamically');
      resolve();
    };
    script.onerror = (err) => {
      console.error('Failed to load Swiper script dynamically:', err);
      reject(err);
    };
    document.head.appendChild(script);
  });

  return swiperPromise;
}

document.addEventListener("click", function (e) {
  const trigger = e.target.closest(".open-related-drawer");
  if (!trigger) return;

  loadSwiperScript().catch((err) => {
    console.error("Error preloading Swiper on related drawer click:", err);
  });

  const drawer = document.getElementById("related-drawer");
  if (!drawer) return;

  const content = drawer.querySelector(".related-drawer__content");

  drawer.dataset.productId = trigger.dataset.productId;
  drawer.dataset.productHandle = trigger.dataset.productHandle;

  drawer.classList.add("open");
  document.body.style.overflow = "hidden";

  // reset tabs (first tab active)
  drawer.querySelectorAll(".related-drawer__tab").forEach((btn, i) => {
    btn.classList.toggle("active", i === 0);
  });

  // load first tab by default
  content.innerHTML = "<p>Loading YOU MAY ALSO LIKE…</p>";
});
// close drawer
document.addEventListener("click", function (e) {
  if (
    e.target.closest(".related-drawer__close") ||
    e.target.classList.contains("related-drawer__overlay")
  ) {
    document.getElementById("related-drawer")?.classList.remove("open");
    document.body.style.overflow = "";
  }
});
// tab switch
document.addEventListener("click", function (e) {
  const tab = e.target.closest(".related-drawer__tab");
  if (!tab) return;

  const drawer = document.getElementById("related-drawer");
  if (!drawer) return;

  const content = drawer.querySelector(".related-drawer__content");
  const activeTab = tab.dataset.tab;

  drawer.querySelectorAll(".related-drawer__tab").forEach((btn) => {
    btn.classList.toggle("active", btn === tab);
  });

  if (activeTab === "also-like") {
    content.innerHTML = "<p>Loading YOU MAY ALSO LIKE…</p>";
  } else {
    content.innerHTML = "<p>Loading PAIR PERFECTLY…</p>";
  }
});
document.addEventListener("click", async function (e) {
  const trigger = e.target.closest(".add-to-cart-drawer");
  if (!trigger) return;

  loadSwiperScript().catch((err) => {
    console.error("Error preloading Swiper on add-to-cart drawer click:", err);
  });

  const handle = trigger.dataset.productHandle;
  const drawer = document.getElementById("add-to-cart-drawer");
  const content = drawer.querySelector(".add-to-cart-drawer__content");

  drawer.classList.add("open");
  document.body.style.overflow = "hidden";
  document.body.classList.add("cart-open");

  try {
    // Fetch product HTML from Shopify section
    const res = await fetch(`/products/${handle}?section_id=quick-buy-drawer`);
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, "text/html");
    const section = doc.querySelector(".shopify-section");

    content.innerHTML = section.innerHTML;

    // Move size guide outside the panel for position fixed
    const sizeGuide = content.querySelector('#size-guide-drawer');
    if (sizeGuide) {
      // Move it to be a sibling of the drawer panel, not inside the content
      const drawerMain = document.getElementById('add-to-cart-drawer');
      if (drawerMain) {
        drawerMain.appendChild(sizeGuide);
      }

      // Re-initialize size guide manually (inline since script already loaded)
      const triggers = document.querySelectorAll('.js-size-guide-trigger');
      const tabs = Array.from(sizeGuide.querySelectorAll('.fit-tab'));
      const contents = Array.from(sizeGuide.querySelectorAll('.size-fit-content'));

      // Reset state
      tabs.forEach(t => {
        t.classList.remove('active');
        t.style.display = 'none';
      });
      contents.forEach(c => c.style.display = 'none');

      // Filter valid fits
      const validFits = contents
        .filter(c => c.querySelector('table[data-unit]'))
        .map(c => c.dataset.fit);

      if (validFits.length > 0) {
        triggers.forEach(el => el.style.display = '');

        // Tab visibility
        tabs.forEach(tab => {
          if (validFits.includes(tab.dataset.fit)) {
            tab.style.display = '';
          }
        });

        // First available fit
        const firstFit = validFits[0];
        sizeGuide.querySelector(`.fit-tab[data-fit="${firstFit}"]`)?.classList.add('active');
        sizeGuide.querySelector(`.size-fit-content[data-fit="${firstFit}"]`)?.style.setProperty('display', 'block');

        // Unit sync
        let currentUnit = 'in';
        const syncUnits = () => {
          contents.forEach(fit => {
            fit.querySelectorAll('table[data-unit]').forEach(table => {
              table.style.display = table.dataset.unit === currentUnit ? 'table' : 'none';
            });
            fit.querySelectorAll('.unitToggle').forEach(t => {
              t.checked = currentUnit === 'cm';
            });
          });
        };
        syncUnits();

        // Tab click handlers
        tabs.forEach(tab => {
          tab.onclick = () => {
            if (tab.style.display === 'none') return;
            tabs.forEach(t => t.classList.remove('active'));
            contents.forEach(c => c.style.display = 'none');
            tab.classList.add('active');
            const activeContent = sizeGuide.querySelector(`.size-fit-content[data-fit="${tab.dataset.fit}"]`);
            if (activeContent) {
              activeContent.style.display = 'block';
              syncUnits();
            }
          };
        });

        // Unit toggle handlers
        sizeGuide.querySelectorAll('.unitToggle').forEach(toggle => {
          toggle.onchange = () => {
            currentUnit = toggle.checked ? 'cm' : 'in';
            syncUnits();
          };
        });
      }
    }

    // Grab variant JSON scripts
    const variantJSONScript = content.querySelector(
      'script[type="application/json"][data-selected-variant]'
    );

    let variants = [];
    const productJSONScript = content.querySelector(
      'script[type="application/json"][data-product-json]'
    );

    if (productJSONScript) {
      try {
        const productData = JSON.parse(productJSONScript.textContent);
        variants = productData.variants;
      } catch (err) {
        console.error("Failed to parse product JSON", err);
      }
    } else {
      // fallback: fetch product JSON dynamically
      try {
        const productRes = await fetch(`/products/${handle}.js`);
        const productData = await productRes.json();
        variants = productData.variants;
      } catch (err) {
        console.error("Failed to fetch product JSON", err);
      }
    }

    // VARIANT RADIO HANDLING
    const variantSelects = content.querySelectorAll('variant-selects input[type="radio"]');

    variantSelects.forEach((input) => {
      input.name = input.name.trim();

      const priceItem = content.querySelector("#price-quick-buy-drawer .price-item");
      if (priceItem) {
        const cleanPrice = priceItem.textContent.replace(/.*(₹\s*\d[\d,\.]*)/, "$1").trim();
        priceItem.textContent = cleanPrice;
      }

      input.addEventListener("change", (e) => {
        const selectedInput = e.target;

        // Uncheck all radios in same group
        const group = content.querySelectorAll(`input[name="${selectedInput.name}"]`);
        group.forEach((radio) => (radio.checked = false));
        selectedInput.checked = true;

        // Update hidden variant input
        const variantIdInput = content.querySelector("input.product-variant-id");
        if (variantIdInput) {
          variantIdInput.value = selectedInput.dataset.variant;
          variantIdInput.dispatchEvent(new Event("change", { bubbles: true }));
          console.log("Hidden variant ID updated to", variantIdInput.value);
        }

        const variantPrice = selectedInput.dataset.price;
        if (priceItem && variantPrice) {
          priceItem.innerHTML = variantPrice;
        }

        if (variantJSONScript && variants.length) {
          const selectedVariantId = parseInt(selectedInput.dataset.variant, 10);
          const variantData = variants.find((v) => v.id === selectedVariantId);
          if (variantData) {
            variantJSONScript.textContent = JSON.stringify(variantData);
            console.log("Variant JSON updated:", variantData);
          }
        }
      });
    });

    // SIZE GUIDE CLOSE BUTTON
    const closeBtn = content.querySelector("#size-guide-drawer .cross-btn");
    if (closeBtn) {
      closeBtn.addEventListener("click", () => {
        document.body.classList.remove("drawer-open");
      });
    }

    // ADD TO CART BUTTON
    const quickBuyButton = content.querySelector("#ProductSubmitButton-quick-buy-drawer");
    if (quickBuyButton) {
      quickBuyButton.addEventListener("click", async function addToCartHandler(event) {
        if (quickBuyButton.classList.contains("is-view-cart")) {
            /* ===== Custom Code Start (Prevent extra cart add on View Cart click) ===== */
            event.preventDefault();
            /* ===== Custom Code End ===== */
            window.location.href = "/cart";
            return;
          }
        event.preventDefault();
        event.stopPropagation();

        const form = quickBuyButton.closest("form");
        if (!form) return;

        const spinner = quickBuyButton.querySelector(".loading__spinner");
        const buttonText = quickBuyButton.querySelector("span");
        if (spinner) spinner.classList.remove("hidden");

        try {
          const formData = new FormData(form);
          const response = await fetch("/cart/add.js", {
            method: "POST",
            body: formData,
          });
          const data = await response.json();

          // Show add-to-cart popup
          const popup = document.getElementById("add-to-cart-popup-box");
          if (popup) {
            popup.hidden = false;
            popup.classList.add("show");
            document.querySelector(".add-to-cart-drawer__overlay")?.classList.add("cart-popup-open");

            const img = popup.querySelector(".add-to-cart-popup-box__img");
            const imageUrl = data.featured_image?.url || data.image;
            if (imageUrl && img) {
              const preloadedImg = new Image();
              preloadedImg.onload = () => {
                img.src = imageUrl;
                img.alt = data.product_title || "";
                popup.hidden = false;
                popup.classList.add("show");
              };
              preloadedImg.src = imageUrl;
            }

            setTimeout(() => {
              popup.hidden = true;
              popup.classList.remove("show");
              document.querySelector(".add-to-cart-drawer__overlay")?.classList.remove("cart-popup-open");
            }, 2500);
          }

          if (spinner) spinner.classList.add("hidden");
          if (buttonText) buttonText.textContent = "View Cart";
           quickBuyButton.classList.add("is-view-cart");

          // quickBuyButton.removeEventListener("click", addToCartHandler);

          // quickBuyButton.addEventListener("click", function continueShoppingHandler(e) {
          //   e.preventDefault();
          //   e.stopPropagation();
          //   drawer.classList.remove("open");
          //   document.body.style.overflow = "";
          // });

          if (window.updateCartCount) window.updateCartCount(data);
        } catch (err) {
          console.error("Add to cart failed:", err);
          if (spinner) spinner.classList.add("hidden");
          if (buttonText) buttonText.textContent = "Add to cart";
          alert("Failed to add product to cart.");
        }
      });
    }

    // SWIPER INIT
    const swiperEl = content.querySelector(".quick-buy__media");
    if (swiperEl && !swiperEl.swiper) {
      /* ===== Page Speed Optimization Start (Conditional Swiper Init) ===== */
      /*
      new Swiper(swiperEl, {
        slidesPerView: 1.5,
        loop: false,
        spaceBetween: 5,
        breakpoints: {
          0: { slidesPerView: 2.5 },
          655: { slidesPerView: 3.5 },
          750: { slidesPerView: 1.5 },
        },
      });
      */
      if (typeof Swiper === 'undefined') {
        await loadSwiperScript().catch(err => console.error("Failed to load Swiper inside drawer init:", err));
      }
      if (typeof Swiper !== 'undefined') {
        new Swiper(swiperEl, {
          slidesPerView: 1.5,
          loop: false,
          spaceBetween: 5,
          breakpoints: {
            0: { slidesPerView: 2.5 },
            655: { slidesPerView: 3.5 },
            750: { slidesPerView: 1.5 },
          },
        });
      }
      /* ===== Page Speed Optimization End ===== */
    }

    // Shopify payment button
    if (window.Shopify && Shopify.PaymentButton) {
      Shopify.PaymentButton.init();
    }
  } catch (err) {
    console.error(err);
    content.innerHTML = "<p>Failed to load product</p>";
  }
});
/* CLOSE DRAWER */
document.addEventListener("click", function (e) {
  if (
    e.target.closest(".add-to-cart-drawer__close") ||
    e.target.classList.contains("add-to-cart-drawer__overlay")
  ) {
    document.getElementById("add-to-cart-drawer")?.classList.remove("open");
    document.body.style.overflow = "";
    document.body.classList.remove('cart-open');
    document.getElementById('wishlistModal').style.display = "none";
  }
});
// blocking the checkout from the back btn 
document.addEventListener('DOMContentLoaded', function () {

  if (location.pathname.includes('/a/storelocator')) {
    document.body.classList.add('store-locator-page');
  }

  const backBtn = document.getElementById("customBackBtn");

  if (backBtn) {
    backBtn.addEventListener("click", function (e) {
      e.preventDefault(); // Stop default browser behavior
      const cameFromCheckout = sessionStorage.getItem('cameFromCheckout');
      const referrer = document.referrer; 

      if (cameFromCheckout === 'true' || referrer.includes('checkouts') || referrer.includes('razorpay')) {
        
        sessionStorage.removeItem('cameFromCheckout');
        window.location.href = "/collections/all";
        
      } else {
        if (window.history.length > 1) {
          window.history.back();
        } else {
          window.location.href = "/"; // Fallback to home if no history
        }
      }
    });
  }
});
document.addEventListener('click', function(e) {
  if (e.target.closest('[name="checkout"]') || e.target.closest('.checkout-button')) {
    sessionStorage.setItem('cameFromCheckout', 'true');
  }
});
$(document).on("click", ".read-more-btn", function (e) {

  e.preventDefault();

  var $btn = $(this);
  var $parent = $btn.closest(".collection-description");

  if (!$parent.length) return;

  var $more = $parent.find(".more-text");
  var $dots = $parent.find(".dots");

  if ($more.is(":visible")) {
    $more.hide();
    $dots.show();
    $btn.text(" Read More");
  } else {
    $more.css("display", "inline");
    $dots.hide();
    $btn.text(" Read Less");
  }

});

// perfectly paired trigger

document.addEventListener('click', function(e) {
  const badge = e.target.closest('.js-scroll-paired');
  if (!badge) return;

  const section = document.querySelector('.lb-bundle-title');

  if (section) {
    const yOffset = -100; // adjust if header overlaps
    const y = section.getBoundingClientRect().top + window.pageYOffset + yOffset;

    window.scrollTo({
      top: y,
      behavior: 'smooth'
    });
  }
});
//  tag show when only perfectly paired section render
function handlePerfectlyPaired() {
  const section = document.querySelector('.lb-bundle-title');
  const badges = document.querySelectorAll('.js-scroll-paired');

  badges.forEach(badge => {
    badge.style.display = section ? 'flex' : 'none';
  });
}

// Run once initially
document.addEventListener('DOMContentLoaded', handlePerfectlyPaired);

// 👇 Watch for async app render
const observer = new MutationObserver(() => {
  const section = document.querySelector('.lb-bundle-title');

  if (section) {
    handlePerfectlyPaired();
    observer.disconnect(); // stop once found
  }
});

// Observe full body (since app injects anywhere)
observer.observe(document.body, {
  childList: true,
  subtree: true
});

// webview setting
  document.addEventListener("DOMContentLoaded", function () {
    // Check if 'webview=true' is in the URL
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('webview') === 'true') {
      // Hide header and footer
      document.querySelector("sticky-header")?.remove();
      document.querySelector(".header")?.remove();
      document.querySelector(".newsletter")?.remove();
      document.querySelector("footer")?.remove();
      document.querySelector(".footer")?.remove();
      document.querySelector(".header-icon-bar")?.remove();
      
      document.addEventListener('contextmenu', function (e) {
        e.preventDefault();
      });
    }
  });

// webview setting